import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PdfUpload } from '../../../components/common/PdfUpload';
import {
  updatePlayerProfile,
  uploadAcademicInfo,
  deleteAcademicInfo
} from '../../../features/playerProfile/playerProfileSlice';
import { CARD, CardTitle, EditButton, InfoGrid, SECONDARY_BUTTON } from './profileCard';

export const AcademicInfo = () => {
  const dispatch = useDispatch();
  const { profile, updateLoading, academicInfoLoading } = useSelector((state) => state.playerProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [academicData, setAcademicData] = useState({
    gpa: '',
    sat: '',
    act: ''
  });
  const [errors, setErrors] = useState({});
  const [transcript, setTranscript] = useState(null);
  const [newTranscriptFile, setNewTranscriptFile] = useState(null);

  // Load profile data when component mounts or profile changes
  useEffect(() => {
    if (profile) {
      setAcademicData({
        gpa: profile.academic_info_gpa ?? profile.gpa ?? '',
        sat: profile.academic_info_sat ?? profile.sat ?? '',
        act: profile.academic_info_act ?? profile.act ?? ''
      });

      // Set transcript if available
      if (profile.acedemicInfo?.url) {
        setTranscript({
          url: profile.acedemicInfo.url,
          filename: profile.acedemicInfo.filename
        });
      } else {
        setTranscript(null);
      }
    }
  }, [profile]);

  const validateField = (field, value) => {
    if (value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    if (field === 'gpa' && (num < 0 || num > 4)) return 'GPA must be between 0 and 4';
    if (field === 'sat' && (num < 400 || num > 1600)) return 'SAT score must be between 400 and 1600';
    if (field === 'act' && (num < 1 || num > 36)) return 'ACT score must be between 1 and 36';
    return '';
  };

  const handleChange = (field, value) => {
    setAcademicData({ ...academicData, [field]: value });
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleTranscriptUpload = (file) => {
    setNewTranscriptFile(file);
    setTranscript({
      filename: file.name,
      isNew: true
    });
  };

  const handleTranscriptDelete = async () => {
    if (window.confirm('Are you sure you want to delete the transcript?')) {
      try {
        await dispatch(deleteAcademicInfo()).unwrap();
        setTranscript(null);
        setNewTranscriptFile(null);
      } catch (error) {
        console.error('Failed to delete transcript:', error);
      }
    }
  };

  const handleSave = async () => {
    // Validate all fields before saving
    const gpaError = validateField('gpa', academicData.gpa);
    const satError = validateField('sat', academicData.sat);
    const actError = validateField('act', academicData.act);
    if (gpaError || satError || actError) {
      setErrors({ gpa: gpaError, sat: satError, act: actError });
      return;
    }

    try {
      // Upload new transcript if one was selected
      if (newTranscriptFile) {
        await dispatch(uploadAcademicInfo(newTranscriptFile)).unwrap();
        setNewTranscriptFile(null);
      }

      // Update academic scores
      await dispatch(updatePlayerProfile({
        academic_info_gpa: parseFloat(academicData.gpa) || 0,
        academic_info_sat: parseInt(academicData.sat) || 0,
        academic_info_act: parseInt(academicData.act) || 0,
      })).unwrap();

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save academic info:', error);
    }
  };

  const handleCancel = () => {
    // Reset to profile data on cancel
    if (profile) {
      setAcademicData({
        gpa: profile.academic_info_gpa ?? profile.gpa ?? '',
        sat: profile.academic_info_sat ?? profile.sat ?? '',
        act: profile.academic_info_act ?? profile.act ?? ''
      });

      // Reset transcript
      if (profile.acedemicInfo?.url) {
        setTranscript({
          url: profile.acedemicInfo.url,
          filename: profile.acedemicInfo.filename
        });
      } else {
        setTranscript(null);
      }
    }
    setNewTranscriptFile(null);
    setErrors({});
    setIsEditing(false);
  };

  const inputClass = (error) =>
    `w-full px-4 py-3 rounded-lg bg-jp-field border ${
      error ? 'border-red-500' : 'border-jp-line'
    } text-jp-ink placeholder:text-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed`;

  return (
    <section className={CARD}>
      <CardTitle title="Academic Information">
        {!isEditing ? (
          <EditButton onClick={() => setIsEditing(true)} />
        ) : (
          <div className="flex gap-2">
            <button onClick={handleCancel} disabled={updateLoading || academicInfoLoading}
              className={`${SECONDARY_BUTTON} disabled:opacity-50`}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={updateLoading || academicInfoLoading}
              className="px-4 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-medium transition-colors disabled:opacity-50 flex items-center">
              {(updateLoading || academicInfoLoading) && (
                <div className="w-3 h-3 mr-1.5 border-2 border-jp-line border-t-transparent rounded-full animate-spin" />
              )}
              Save
            </button>
          </div>
        )}
      </CardTitle>

      {/* Two-column layout: scores left, transcript right */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Left — GPA, SAT Score, ACT Score in one row */}
        <div>
          <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-3">Scores</p>
          {!isEditing ? (
            <InfoGrid
              items={[
                { label: 'GPA', value: academicData.gpa },
                { label: 'SAT Score', value: academicData.sat },
                { label: 'ACT Score', value: academicData.act },
              ]}
            />
          ) : (
          <div className="grid grid-cols-3 gap-3">
            {/* GPA */}
            <div>
              <label className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">GPA</label>
              <input
                type="number" step="0.01" min="0" max="4"
                value={academicData.gpa}
                onChange={(e) => handleChange('gpa', e.target.value)}
                disabled={!isEditing}
                placeholder="0.0–4.0"
                className={inputClass(errors.gpa)}
              />
              {errors.gpa && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.gpa}</p>}
            </div>
            {/* SAT */}
            <div>
              <label className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">SAT Score</label>
              <input
                type="number" min="400" max="1600"
                value={academicData.sat}
                onChange={(e) => handleChange('sat', e.target.value)}
                disabled={!isEditing}
                placeholder="400–1600"
                className={inputClass(errors.sat)}
              />
              {errors.sat && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.sat}</p>}
            </div>
            {/* ACT */}
            <div>
              <label className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">ACT Score</label>
              <input
                type="number" min="1" max="36"
                value={academicData.act}
                onChange={(e) => handleChange('act', e.target.value)}
                disabled={!isEditing}
                placeholder="1–36"
                className={inputClass(errors.act)}
              />
              {errors.act && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.act}</p>}
            </div>
          </div>
          )}
        </div>

        {/* Right — Academic Transcript */}
        <div>
          <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-3">Academic Transcript</p>
          {transcript && !isEditing ? (
            <a href={transcript.url || transcript.documentUrl} target="_blank" rel="noopener noreferrer" className="block group">
              <div className="relative">
                <PdfUpload
                  label=""
                  file={transcript}
                  onFileChange={handleTranscriptUpload}
                  accept=".pdf"
                  placeholder="Drop your transcript PDF here or click to browse"
                  disabled={true}
                  viewOnly={true}
                  loading={academicInfoLoading}
                  themed
                />
                <div className="absolute inset-0 bg-jp-accent/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center pointer-events-none">
                  <div className="bg-cyan-500 rounded-full p-3 shadow-lg">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </a>
          ) : (
            <>
              <PdfUpload
                label=""
                file={transcript}
                onFileChange={handleTranscriptUpload}
                accept=".pdf"
                placeholder="Drop your transcript PDF here or click to browse"
                disabled={!isEditing}
                loading={academicInfoLoading}
                themed
              />
              {transcript && isEditing && (
                <button
                  onClick={handleTranscriptDelete}
                  disabled={academicInfoLoading}
                  className="mt-3 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center"
                >
                  {academicInfoLoading && (
                    <div className="w-4 h-4 mr-2 border-2 border-jp-line border-t-transparent rounded-full animate-spin" />
                  )}
                  Delete Transcript
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AcademicInfo;
